import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { setSessionWithExpiry } from '../helpers/sessionUtils';
import { useVendorAuth } from '../../context/VendorAuthContext';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import SEO from "../../components/common/SEO";

const LOGIN_OTP_RESOURCES = [
    { type: 'link', rel: 'stylesheet', href: '/assets/vendor/font-awesome/css/fontawesome-all.min.css', id: 'loginotp-page-fa' },
    { type: 'link', rel: 'preconnect', href: 'https://fonts.googleapis.com', id: 'loginotp-page-gfont-preconnect1' },
    { type: 'link', rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'true', id: 'loginotp-page-gfont-preconnect2' },
    { type: 'link', rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=Poppins:ital,wght=0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900&display=swap', id: 'loginotp-page-gfont-style' }
];

const FormMessageDisplay = ({ message, type }) => {
    if (!message) return null;
    const alertStyle = {
        padding: '12px 16px',
        borderRadius: '8px',
        fontSize: '0.9rem',
        fontWeight: '500',
        marginBottom: '20px',
        textAlign: 'left',
        border: '1px solid transparent',
    };
    const style = type === 'error' ? {
        ...alertStyle,
        backgroundColor: '#fee2e2',
        color: '#ef4444',
        borderColor: '#fca5a5'
    } : (type === 'success' ? {
        ...alertStyle,
        backgroundColor: '#e6f9f3',
        color: '#0bc184',
        borderColor: '#a7f3d0'
    } : {
        ...alertStyle,
        backgroundColor: '#eff6ff',
        color: '#25a5f0',
        borderColor: '#bfdbfe'
    });
    return (<div style={style} role="alert">{message}</div>);
};

function LoginOTP() {
    const navigate = useNavigate();
    const { login } = useVendorAuth();

    const [currentUiStep, setCurrentUiStep] = useState(1);
    const [phone, setPhone] = useState('');
    const [otp, setOtp] = useState(Array(6).fill(''));
    const [userIdForOtp, setUserIdForOtp] = useState(null);

    const [timeLeft, setTimeLeft] = useState(30);
    const [isTimerRunning, setIsTimerRunning] = useState(false);
    const [canResendOtp, setCanResendOtp] = useState(false);
    const otpTimerIntervalRef = useRef(null);
    const otpInputsRef = useRef([]);

    const [isLoading, setIsLoading] = useState(false);
    const [formMessage, setFormMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        document.title = "Login with Mobile Number - Mangoloops";
        LOGIN_OTP_RESOURCES.forEach(attrs => {
            if (!document.getElementById(attrs.id)) {
                const el = document.createElement(attrs.type);
                Object.keys(attrs).forEach(key => { if (key !== 'type') el[key] = attrs[key]; });
                document.head.appendChild(el);
            }
        });
        return () => {
            LOGIN_OTP_RESOURCES.forEach(attrs => {
                const el = document.getElementById(attrs.id);
                if (el) el.remove();
            });
        };
    }, []);

    useEffect(() => {
        if (isTimerRunning && timeLeft > 0) {
            otpTimerIntervalRef.current = setInterval(() => { setTimeLeft(prev => prev - 1); }, 1000);
        } else if (timeLeft <= 0) {
            clearInterval(otpTimerIntervalRef.current);
            setIsTimerRunning(false);
            setCanResendOtp(true);
        }
        return () => clearInterval(otpTimerIntervalRef.current);
    }, [isTimerRunning, timeLeft]);

    const handleOtpChange = (e, index) => {
        setFormMessage({ type: '', text: '' });
        const { value } = e.target;
        const digit = value.slice(-1).replace(/[^0-9]/g, '');
        const newOtp = [...otp];
        newOtp[index] = digit;
        setOtp(newOtp);

        if (digit && index < otpInputsRef.current.length - 1) {
            otpInputsRef.current[index + 1]?.focus();
        } else if (digit && index === 5) {
            if (newOtp.join('').length === 6) {
                handleVerifyOtpAndLogin(newOtp.join(''));
            }
        }
    };

    const handleOtpKeyDown = (e, index) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            otpInputsRef.current[index - 1]?.focus();
        }
        if (!/[0-9]|Backspace|Tab|ArrowLeft|ArrowRight|Delete|Enter/.test(e.key)) {
            e.preventDefault();
        }
    };

    const handleRequestOtpForLogin = async (e) => {
        e.preventDefault();
        setFormMessage({ type: '', text: '' });
        if (!phone || phone.length !== 10) {
            setFormMessage({ type: 'error', text: "Please enter a valid 10-digit mobile number." });
            return;
        }
        setIsLoading(true);

        let phoneForBackend = '91' + phone;

        try {
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
            const response = await axios.post(`${API_BASE_URL}/vendors/check-mobile-otp-login`, { phone: phoneForBackend });
            const { status, message, userId } = response.data;
            if (status === 'otp_sent') {
                setFormMessage({ type: 'success', text: message || "OTP sent successfully!" });
                setUserIdForOtp(userId);
                setOtp(Array(6).fill(''));
                setTimeLeft(30);
                setIsTimerRunning(true);
                setCanResendOtp(false);
                setCurrentUiStep(2);
                setTimeout(() => otpInputsRef.current[0]?.focus(), 0);
            }
        } catch (error) {
            const errorData = error.response?.data;
            const errorMsg = errorData?.message || "Failed to request OTP. Please try again.";
            setFormMessage({ type: 'error', text: errorMsg });
            if (errorData?.status === 'not_found_redirect_to_registration') {
                setTimeout(() => navigate(`/registration?phone=${encodeURIComponent(errorData.phone || phone)}`), 3000);
            } else if (errorData?.status === 'not_verified_redirect_to_registration') {
                setTimeout(() => navigate('/admin/kyc-verification'), 3000);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendOtpForLogin = async () => {
        if (!canResendOtp || isLoading || !phone) return;
        setFormMessage({ type: '', text: '' });
        setIsLoading(true);

        let phoneForBackend = '91' + phone;

        try {
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
            const response = await axios.post(`${API_BASE_URL}/vendors/check-mobile-otp-login`, { phone: phoneForBackend });
            const { status, message, userId } = response.data;

            if (status === 'otp_sent') {
                setFormMessage({ type: 'success', text: message || "A fresh OTP has been sent!" });
                setUserIdForOtp(userId);
                setOtp(Array(6).fill(''));
                setTimeLeft(30);
                setIsTimerRunning(true);
                setCanResendOtp(false);
                setTimeout(() => otpInputsRef.current[0]?.focus(), 0);
            }
        } catch (error) {
            setFormMessage({ type: 'error', text: error.response?.data?.message || "OTP resend failed." });
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyOtpAndLogin = async (otpOverride = null) => {
        setFormMessage({ type: '', text: '' });
        const enteredOtp = otpOverride || otp.join('');
        if (enteredOtp.length !== 6) {
            setFormMessage({ type: 'error', text: "Please enter the 6-digit OTP." });
            return;
        }
        if (!userIdForOtp) {
            setFormMessage({ type: 'error', text: "User session error." });
            setCurrentUiStep(1);
            return;
        }
        setIsLoading(true);
        try {
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
            const response = await axios.post(`${API_BASE_URL}/vendors/verify-otp-login`, { userId: userIdForOtp, otp: enteredOtp });
            const { status, message, token, user } = response.data;
            if (status === 'otp_verified_proceed_to_login' && token && user) {
                setSessionWithExpiry(response.data);
                localStorage.setItem('authToken', token);
                localStorage.setItem('userInfo', JSON.stringify(user));
                setFormMessage({ type: 'success', text: message || "Verification successful! Redirecting..." });
                clearInterval(otpTimerIntervalRef.current);
                setIsTimerRunning(false);
                await login(token, user);
            } else {
                setFormMessage({ type: 'error', text: message || "OTP verification failed." });
                setOtp(Array(6).fill(''));
                if (otpInputsRef.current[0]) otpInputsRef.current[0].focus();
            }
        } catch (error) {
            setFormMessage({ type: 'error', text: error.response?.data?.message || "Invalid OTP or verification error." });
            setOtp(Array(6).fill(''));
            if (otpInputsRef.current[0]) otpInputsRef.current[0].focus();
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <style>{`
                :root {
                    --primary: #4f46e5;
                    --primary-hover: #4338ca;
                    --secondary-blue: #25a5f0;
                    --secondary-blue-hover: #1d8fd6;
                    --success: #0bc184;
                    --danger: #ef4444;
                    --border-color: #cbd5e1;
                    --text-dark: #1f2937;
                    --text-muted: #6b7280;
                    --bg-color: #f8fafc;
                }

                .login-page-body {
                    font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
                    background-color: var(--bg-color);
                    color: var(--text-dark);
                    margin: 0;
                    padding: 20px; 
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    min-height: 100vh;
                    box-sizing: border-box;
                }

                .portal-wrapper {
                    width: 100%;
                    max-width: 460px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                .logo-container {
                    margin-bottom: 22px; 
                    text-align: center;
                }

                .logo-container img {
                    max-width: 210px; 
                    height: auto;
                    display: block;
                }

                .auth-card {
                    background: #ffffff;
                    width: 100%;
                    padding: 35px; 
                    border-radius: 16px;
                    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.05);
                    border: 1px solid #e5e7eb;
                    box-sizing: border-box;
                }

                .header h2 {
                    font-size: 1.85rem;
                    margin: 0 0 6px 0;
                    font-weight: 700;
                    color: #111827;
                }

                .header p {
                    color: var(--text-muted);
                    margin-bottom: 26px; 
                    font-size: 0.95rem;
                    line-height: 1.4;
                }

                .form-group {
                    margin-bottom: 20px;
                    position: relative;
                }

                label {
                    display: block;
                    font-weight: 600;
                    margin-bottom: 8px;
                    font-size: 0.85rem;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    color: #374151;
                }

                .btn {
                    width: 100%;
                    border: none;
                    padding: 14px;
                    font-size: 1rem;
                    font-weight: 600;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: background 0.2s, opacity 0.2s;
                    box-sizing: border-box;
                }

                .btn-blue-action {
                    background-color: var(--secondary-blue);
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                }
                .btn-blue-action:hover { background-color: var(--secondary-blue-hover); }

                .btn-primary-wizard {
                    background-color: var(--primary);
                    color: white;
                }
                .btn-primary-wizard:hover { background-color: var(--primary-hover); }

                .footer-switch-text {
                    text-align: center;
                    margin-top: 25px;
                    font-size: 0.95rem;
                    color: var(--text-dark);
                }
                .footer-switch-text a {
                    color: var(--secondary-blue);
                    text-decoration: none;
                    font-weight: 600;
                    cursor: pointer;
                }
                .footer-switch-text a:hover { text-decoration: underline; }

                /* OTP Box Configurations */
                .otp-inputs { 
                    display: flex; 
                    justify-content: space-between; 
                    gap: 10px; 
                    margin: 25px 0 15px 0; 
                }
                .otp-box { 
                    width: 48px; 
                    height: 52px; 
                    text-align: center; 
                    font-size: 1.5rem; 
                    font-weight: 700; 
                    border: 1px solid var(--border-color); 
                    border-radius: 8px; 
                }
                .otp-box:focus {
                    outline: none;
                    border-color: var(--primary);
                    box-shadow: 0 0 0 4px rgba(79, 70, 229, 0.12);
                }

                .status-container { 
                    margin-top: 15px; 
                    font-size: 0.9rem; 
                    font-weight: 500; 
                    text-align: left;
                }
                .timer-text { color: var(--text-muted); }
                .expired-alert { color: var(--danger); font-weight: 600; }
                .resend-btn { 
                    color: var(--primary); 
                    background: none; 
                    border: none; 
                    padding: 0; 
                    font-size: 0.9rem; 
                    font-weight: 600; 
                    text-decoration: underline; 
                    cursor: pointer; 
                }

                /* Phone Input Styles Override */
                .PhoneInput {
                    width: 100%;
                    border: 1px solid var(--border-color);
                    border-radius: 8px;
                    padding: 10px 14px;
                    box-sizing: border-box;
                    background-color: white;
                    display: flex;
                    align-items: center;
                }
                .PhoneInputCountry {
                    margin-right: 10px;
                }
                .PhoneInputInput {
                    border: none !important;
                    outline: none !important;
                    font-size: 1rem;
                    width: 100%;
                    color: var(--text-dark);
                }
            `}</style>
            <SEO title="Login with OTP | Mangoloops" description="Access your vendor billing dashboard securely via mobile phone verification." />

            <div className="login-page-body">
                <div className="portal-wrapper">

                    <div className="logo-container">
                        <Link to="/">
                            <img src="/logo-mangoloops.png" alt="MangoLoops Logo" />
                        </Link>
                    </div>

                    <div className="auth-card">
                        {currentUiStep === 1 && (
                            <div id="loginOtpPanel" className="auth-panel active">
                                <div className="header">
                                    <h2>Login with OTP</h2>
                                    <p>Access your billing dashboard securely via instant token.</p>
                                </div>

                                <FormMessageDisplay message={formMessage.text} type={formMessage.type} />

                                <form onSubmit={handleRequestOtpForLogin}>
                                    <div className="form-group">
                                    <label>Mobile Number</label>
                                    <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--border-color)', borderRadius: '8px', background: '#fff', overflow: 'hidden', transition: 'all 0.2s' }}>
                                        <span style={{ padding: '14px', background: '#f8fafc', borderRight: '1px solid var(--border-color)', fontWeight: 600, color: 'var(--text-dark)' }}>+91</span>
                                        <input
                                            type="text"
                                            placeholder="Enter 10-digit mobile number"
                                            value={phone}
                                            onChange={(e) => { 
                                                setFormMessage({ type: '', text: '' }); 
                                                const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                                                setPhone(val); 
                                            }}
                                            disabled={isLoading}
                                            required
                                            style={{ border: 'none', padding: '14px', outline: 'none', width: '100%', fontSize: '1rem', color: 'var(--text-dark)' }}
                                        />
                                    </div>
                                </div>
                                    <button type="submit" className="btn btn-blue-action" style={{ marginTop: '10px' }} disabled={isLoading}>
                                        {isLoading ? 'Requesting...' : 'Request Secure Access OTP'}
                                    </button>
                                </form>
                                <div className="footer-switch-text">
                                    Back to standard <Link to="/login">Password Login</Link>
                                </div>
                            </div>
                        )}

                        {currentUiStep === 2 && (
                            <div id="otpVerifyPanel" className="auth-panel active">
                                <div className="header">
                                    <h2>OTP Verification</h2>
                                    <p>Please enter the OTP (One-Time Password) sent to your registered mobile number (<span style={{ fontWeight: 600, color: '#111827' }}>{phone}</span>) to complete verification.</p>
                                </div>

                                <FormMessageDisplay message={formMessage.text} type={formMessage.type} />

                                <form onSubmit={(e) => { e.preventDefault(); handleVerifyOtpAndLogin(); }}>
                                    <div className="otp-inputs">
                                        {otp.map((digit, index) => (
                                            <input
                                                key={index}
                                                ref={el => otpInputsRef.current[index] = el}
                                                type="text"
                                                className="otp-box"
                                                maxLength="1"
                                                value={digit}
                                                onChange={(e) => handleOtpChange(e, index)}
                                                onKeyDown={(e) => handleOtpKeyDown(e, index)}
                                                required
                                                pattern="[0-9]"
                                                disabled={isLoading}
                                            />
                                        ))}
                                    </div>

                                    <div className="status-container">
                                        {isTimerRunning ? (
                                            <div className="timer-text">
                                                Resend code in <span style={{ fontWeight: 600 }}>{timeLeft}</span> seconds
                                            </div>
                                        ) : (
                                            <div>
                                                <span className="expired-alert">⚠️ Code expired. </span>
                                                <button type="button" className="resend-btn" onClick={handleResendOtpForLogin} disabled={isLoading}>
                                                    Resend OTP
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    <button type="submit" className="btn btn-primary-wizard" style={{ marginTop: '25px' }} disabled={isLoading || otp.join('').length !== 6}>
                                        {isLoading ? 'Verifying...' : 'Verify Code'}
                                    </button>
                                </form>
                                <div className="footer-switch-text">
                                    <span style={{ cursor: 'pointer', color: 'var(--secondary-blue)', fontWeight: 600 }} onClick={() => { setCurrentUiStep(1); setFormMessage({ type: '', text: '' }); }}>
                                        Change Phone Number
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default LoginOTP;
